import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckBadgeIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { LoadingButton } from "@mui/lab";
import PMListboxFormik from "../../../utils/PMListboxFormik";
import { listUpdateServiceAgreementAsync } from "../../../../slices/service/agreements";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { ServiceAgreementTypeSelector } from "../../../utils/moduleSelectors/serviceAgreementTypeSelector";
import { serviceAgreementIntervals } from "./utils";
import {
  agreementDisplayLabel,
  buildAgreementListUpdatePayload,
  collectChangedMassEditFields,
} from "../../../../utils/serviceAgreementMassEdit";

import { runListUpdateAgreementsOperation } from "./runListUpdateAgreementsOperation";

export default function ServiceAgreementMultiEditDialog(props) {
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.session?.currentCompany);
  const { t } = useTranslation();
  const { open, onClose, onRefresh, selectedItems = [] } = props;

  const [confirmPrompt, setConfirmPrompt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogStatus, setDialogStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      setConfirmPrompt(false);
      setDialogStatus(null);
      setSubmitting(false);
    } else {
      setDialogStatus(null);
    }
  }, [open]);

  const fieldLabels = useMemo(
    () => ({
      customer_id: t("service.agreements.form.customer"),
      project_id: t("service.agreements.form.project"),
      agreement_type_id: t("service.agreements.form.section_type"),
      interval_id: t("service.agreements.multi_edit.field.interval"),
    }),
    [t]
  );

  const intervalOptions = useMemo(
    () => [
      {
        id: null,
        value: null,
        title: t("service.agreements.multi_edit.field.unchanged"),
        label: t("service.agreements.multi_edit.field.unchanged"),
      },
      ...serviceAgreementIntervals.map((opt) => ({
        ...opt,
        title: opt.label,
      })),
    ],
    [t]
  );

  const agreementLabels = useMemo(
    () => selectedItems.map((item) => agreementDisplayLabel(item)),
    [selectedItems]
  );

  const handleClose = () => {
    if (typeof onClose === "function") onClose();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="mt-3 sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {t("service.agreements.multi_edit.title", {
                      count: selectedItems.length,
                    })}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-slate-500">
                    {t("service.agreements.multi_edit.subtitle")}
                  </p>

                  <Formik
                    initialValues={{
                      ids: selectedItems.map((item) => String(item.id)),
                      customer_id: "",
                      project_id: "",
                      agreement_type_id: "",
                      interval_id: null,
                      submit: null,
                    }}
                    enableReinitialize
                    validationSchema={Yup.object({})}
                    onSubmit={async (values, helpers) => {
                      const payload = buildAgreementListUpdatePayload(values);
                      if (!payload.ids.length) {
                        setDialogStatus({
                          type: "error",
                          message: t(
                            "service.agreements.multi_edit.error.empty_selection"
                          ),
                        });
                        helpers.setSubmitting(false);
                        return;
                      }
                      if (!Object.keys(payload.agreement).length) {
                        setDialogStatus({
                          type: "error",
                          message: t(
                            "service.agreements.multi_edit.error.no_fields"
                          ),
                        });
                        helpers.setSubmitting(false);
                        return;
                      }

                      setSubmitting(true);
                      setDialogStatus({
                        type: "pending",
                        message: t("service.agreements.multi_edit.saving"),
                      });

                      try {
                        const result = await runListUpdateAgreementsOperation({
                          dispatch,
                          listUpdateAsync: listUpdateServiceAgreementAsync,
                          values,
                          companyId: currentCompany?.id,
                          onRefresh,
                          t,
                        });
                        setDialogStatus(null);
                        if (result.ok) handleClose();
                      } catch (err) {
                        setDialogStatus({
                          type: "error",
                          message:
                            err?.message ||
                            t("service.agreements.multi_edit.error.save"),
                        });
                      } finally {
                        setSubmitting(false);
                        helpers.setSubmitting(false);
                      }
                    }}
                  >
                    {(formik) => {
                      const changes = collectChangedMassEditFields(
                        formik.values,
                        fieldLabels
                      );
                      const canConfirm = changes.length > 0;

                      return (
                        <Form>
                          {dialogStatus ? (
                            <div
                              role={
                                dialogStatus.type === "error"
                                  ? "alert"
                                  : "status"
                              }
                              className={`mt-4 rounded-xl px-3 py-2 text-sm ${
                                dialogStatus.type === "error"
                                  ? "bg-red-50 text-red-800"
                                  : dialogStatus.type === "success"
                                    ? "bg-emerald-50 text-emerald-800"
                                    : "bg-slate-50 text-slate-700"
                              }`}
                            >
                              {dialogStatus.message}
                            </div>
                          ) : null}

                          {!confirmPrompt ? (
                            <div className="mt-6 space-y-6">
                              <div>
                                <h4 className="text-sm font-semibold text-slate-800">
                                  {t(
                                    "service.agreements.multi_edit.selected_heading"
                                  )}
                                </h4>
                                <ul className="mt-2 max-h-36 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-slate-600">
                                  {agreementLabels.map((label, idx) => (
                                    <li key={`${label}-${idx}`}>{label}</li>
                                  ))}
                                </ul>
                              </div>

                              <div className="grid grid-cols-6 gap-6">
                                <div className="col-span-6">
                                  <CustomerSelector
                                    label={t(
                                      "service.agreements.form.customer"
                                    )}
                                    {...formik}
                                  />
                                </div>
                                <div className="col-span-6">
                                  <ProjectSelector
                                    label={t(
                                      "service.agreements.form.project"
                                    )}
                                    {...formik}
                                  />
                                </div>
                                <div className="col-span-6">
                                  <ServiceAgreementTypeSelector
                                    label={t(
                                      "service.agreements.form.section_type"
                                    )}
                                    name="agreement_type_id"
                                    {...formik}
                                  />
                                </div>
                                <div className="col-span-6">
                                  <PMListboxFormik
                                    label={t(
                                      "service.agreements.multi_edit.field.interval"
                                    )}
                                    name="interval_id"
                                    options={intervalOptions}
                                    {...formik}
                                  />
                                </div>
                              </div>

                              <p className="text-xs text-slate-500">
                                {t(
                                  "service.agreements.multi_edit.unchanged_hint"
                                )}
                              </p>
                            </div>
                          ) : (
                            <div className="px-4 py-8">
                              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                                <ExclamationCircleIcon
                                  className="h-6 w-6 text-amber-700"
                                  aria-hidden="true"
                                />
                              </div>
                              <div className="mt-3 text-center sm:mt-5">
                                <Dialog.Title
                                  as="h3"
                                  className="text-base font-semibold leading-6 text-gray-900"
                                >
                                  {t(
                                    "service.agreements.multi_edit.confirm_title"
                                  )}
                                </Dialog.Title>
                                <div className="mt-3 space-y-3 text-left text-sm text-slate-600">
                                  <p>
                                    {t(
                                      "service.agreements.multi_edit.confirm_body",
                                      { count: selectedItems.length }
                                    )}
                                  </p>
                                  <div>
                                    <p className="font-semibold text-slate-800">
                                      {t(
                                        "service.agreements.multi_edit.fields_heading"
                                      )}
                                    </p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5">
                                      {changes.map((c) => (
                                        <li key={c.key}>
                                          {c.label}:{" "}
                                          <span className="font-medium text-slate-900">
                                            {String(c.value)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-800">
                                      {t(
                                        "service.agreements.multi_edit.selected_heading"
                                      )}
                                    </p>
                                    <ul className="mt-1 max-h-28 list-disc space-y-1 overflow-y-auto pl-5">
                                      {agreementLabels.map((label, idx) => (
                                        <li key={`confirm-${label}-${idx}`}>
                                          {label}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                              type="button"
                              className="inline-flex justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={() => {
                                if (confirmPrompt) {
                                  setConfirmPrompt(false);
                                  return;
                                }
                                handleClose();
                              }}
                              disabled={submitting}
                            >
                              {confirmPrompt
                                ? t("service.agreements.multi_edit.back")
                                : t("Close")}
                            </button>

                            {!confirmPrompt ? (
                              <button
                                type="button"
                                className="rounded bg-blue-500 px-4 py-2 text-white shadow disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={!canConfirm || submitting}
                                onClick={() => {
                                  setDialogStatus(null);
                                  setConfirmPrompt(true);
                                }}
                              >
                                {t("Update")}
                              </button>
                            ) : (
                              <LoadingButton
                                loading={submitting}
                                disabled={submitting || !canConfirm}
                                loadingPosition="start"
                                startIcon={<CheckBadgeIcon />}
                                type="button"
                                variant="contained"
                                color="success"
                                onClick={formik.submitForm}
                              >
                                {t("Confirm")}
                              </LoadingButton>
                            )}
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

ServiceAgreementMultiEditDialog.defaultProps = {
  selected: null,
  readOnly: false,
  open: false,
  selectedItems: [],
  onRefresh: () => null,
  onClose: () => null,
};
